import * as XLSX from "xlsx";

// <handle>@<psp>, mirrors the backend validation
export const VPA_REGEX = /^[a-zA-Z0-9._-]{2,}@[a-zA-Z0-9]{2,}$/;

export const MAX_SHEET_BYTES = 5 * 1024 * 1024;

export interface ParsedVpaSheet {
  vpas: string[];
  errors: string[];
}

/**
 * Reads the first column of the first sheet as a list of VPAs. A first-row cell
 * of "vpa" is treated as a header. Any invalid row is reported (by sheet row
 * number) so the caller can reject the whole file - nothing is partially used.
 * Duplicates (after normalisation) are collapsed.
 */
export const parseVpaSheet = async (
  file: File,
  { maxLength, lowercase }: { maxLength: number; lowercase: boolean },
): Promise<ParsedVpaSheet> => {
  if (file.size > MAX_SHEET_BYTES) return { vpas: [], errors: ["File is larger than 5 MB."] };

  let rows: unknown[][];
  try {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: false, blankrows: true });
  } catch {
    return { vpas: [], errors: ["Could not read the file. Please upload a valid .xlsx file."] };
  }

  const vpas: string[] = [];
  const seen = new Set<string>();
  const errors: string[] = [];

  rows.forEach((row, index) => {
    const raw = String(row[0] ?? "").trim();
    if (!raw) return;
    if (index === 0 && raw.toLowerCase() === "vpa") return; // header

    const vpa = lowercase ? raw.toLowerCase() : raw;
    if (vpa.length > maxLength || !VPA_REGEX.test(vpa)) {
      errors.push(`Row ${index + 1}: "${raw}" is not a valid VPA (e.g. handle@psp, max ${maxLength} characters).`);
      return;
    }
    if (!seen.has(vpa.toLowerCase())) {
      seen.add(vpa.toLowerCase());
      vpas.push(vpa);
    }
  });

  if (!errors.length && !vpas.length) errors.push("No VPAs found in the first column of the first sheet.");
  return { vpas, errors };
};

export const downloadVpaTemplate = () => {
  const sheet = XLSX.utils.aoa_to_sheet([["vpa"], ["handle@psp"]]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "VPAs");
  XLSX.writeFile(workbook, "program_vpa_upload_template.xlsx");
};
